/**
 * 이미지 캐싱 서비스
 * 이미지를 미리 로드하고 캐시하여 성능을 개선합니다.
 */
import { isExternalProfileImageSource } from '../utils/memberProfileDisplay';

interface CacheEntry {
  url: string;
  blob?: Blob;
  objectUrl?: string;
  isExternal?: boolean;
  timestamp: number;
}

class ImageCacheService {
  private cache = new Map<string, CacheEntry>();
  private readonly CACHE_SIZE_LIMIT = 50; // 최대 캐시 개수
  private readonly CACHE_EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24시간 (밀리초)

  /**
   * 이미지를 캐시에서 가져오거나 새로 로드합니다.
   */
  async getImage(imageId: string): Promise<string> {
    const cacheKey = this.getCacheKey(imageId);
    const cached = this.cache.get(cacheKey);

    // 캐시에 있고 유효한 경우
    if (cached && this.isValidCacheEntry(cached)) {
      const age = Date.now() - cached.timestamp;
      const ageSeconds = Math.floor(age / 1000);
      console.log(`📸 [캐시 로드] 이미지 ID: ${imageId}, 캐시 나이: ${ageSeconds}초, 캐시 크기: ${this.cache.size}/${this.CACHE_SIZE_LIMIT}`);
      return this.getEntryUrl(cached);
    }

    // 캐시에 없거나 만료된 경우 새로 로드
    if (cached) {
      console.log(`⏰ [캐시 만료] 이미지 ID: ${imageId}, 새로 로드 필요`);
    } else {
      console.log(`📥 [캐시 미스] 이미지 ID: ${imageId}, 네트워크에서 다운로드`);
    }
    return await this.loadAndCacheImage(imageId);
  }

  /**
   * 이미지를 미리 로드하여 캐시에 저장합니다.
   */
  async preloadImages(imageIds: string[]): Promise<void> {
    console.log(`🔄 이미지 프리로드 시작: ${imageIds.length}개`);
    
    const loadPromises = imageIds.map(imageId => 
      this.getImage(imageId).catch(error => {
        console.warn(`⚠️ 이미지 프리로드 실패: ${imageId}`, error);
      })
    );

    await Promise.allSettled(loadPromises);
    console.log(`✅ 이미지 프리로드 완료`);
  }

  /**
   * 특정 이미지를 캐시에서 제거합니다.
   */
  removeFromCache(imageId: string): void {
    const cacheKey = this.getCacheKey(imageId);
    const cached = this.cache.get(cacheKey);
    
    if (cached) {
      this.releaseEntryResources(cached);
      this.cache.delete(cacheKey);
      console.log(`🗑️ 이미지 캐시 제거: ${imageId}`);
    }
  }

  /**
   * 만료된 캐시 항목들을 정리합니다.
   */
  cleanupExpiredCache(): void {
    const expiredKeys: string[] = [];

    this.cache.forEach((entry, key) => {
      if (!this.isValidCacheEntry(entry)) {
        expiredKeys.push(key);
      }
    });

    expiredKeys.forEach(key => {
      const entry = this.cache.get(key);
      if (entry) {
        this.releaseEntryResources(entry);
        this.cache.delete(key);
      }
    });

    if (expiredKeys.length > 0) {
      console.log(`🧹 만료된 캐시 정리: ${expiredKeys.length}개`);
    }
  }

  /**
   * 캐시 크기가 제한을 초과하면 오래된 항목들을 제거합니다.
   */
  private enforceCacheSizeLimit(): void {
    if (this.cache.size <= this.CACHE_SIZE_LIMIT) return;

    // 타임스탬프 기준으로 정렬하여 오래된 항목부터 제거
    const entries = Array.from(this.cache.entries())
      .sort(([, a], [, b]) => a.timestamp - b.timestamp);

    const removeCount = this.cache.size - this.CACHE_SIZE_LIMIT;
    for (let i = 0; i < removeCount; i++) {
      const [key, entry] = entries[i];
      this.releaseEntryResources(entry);
      this.cache.delete(key);
    }

    console.log(`📦 캐시 크기 제한 적용: ${removeCount}개 제거`);
  }

  /**
   * 이미지를 로드하고 캐시에 저장합니다.
   * 주의: 이 함수는 캐시를 확인하지 않습니다. getImage()를 먼저 호출하여 캐시를 확인하세요.
   */
  private async loadAndCacheImage(imageId: string): Promise<string> {
    const cacheKey = this.getCacheKey(imageId);
    
    // 캐시에 이미 있는지 다시 한 번 확인 (중복 로드 방지)
    const existingCache = this.cache.get(cacheKey);
    if (existingCache && this.isValidCacheEntry(existingCache)) {
      console.log(`📸 [캐시 재확인] 이미지 ID: ${imageId} 이미 캐시에 존재, 재사용`);
      return this.getEntryUrl(existingCache);
    }
    
    const isExternalSource = isExternalProfileImageSource(imageId);
    // 외부 URL(사진 프로필)은 fetch 강제 다운로드 대신 브라우저 캐시를 활용
    if (isExternalSource) {
      this.cache.set(cacheKey, {
        url: imageId,
        isExternal: true,
        timestamp: Date.now()
      });
      this.enforceCacheSizeLimit();
      console.log(`💾 [외부 URL 캐시] 이미지 ID: ${imageId}, 캐시 크기: ${this.cache.size}/${this.CACHE_SIZE_LIMIT}`);
      return imageId;
    }

    // 기본 SVG는 blob으로 캐시
    const fileName = imageId;
    const imageUrl = `/profile/${fileName}.svg`;

    try {
      // 이미지 다운로드
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const cacheEntry: CacheEntry = {
        url: imageUrl,
        blob,
        objectUrl,
        timestamp: Date.now()
      };

      // 캐시에 저장
      this.cache.set(cacheKey, cacheEntry);
      
      // 캐시 크기 제한 적용
      this.enforceCacheSizeLimit();

      console.log(`💾 [캐시 저장] 이미지 ID: ${imageId}, 원본: ${fileName}.svg, 캐시 크기: ${this.cache.size}/${this.CACHE_SIZE_LIMIT}`);
      
      return objectUrl;
    } catch (error) {
      console.error(`❌ 이미지 로드 실패: ${imageId}`, error);
      // 로드 실패 시 기본 이미지 반환
      return await this.getDefaultImageUrl();
    }
  }

  /**
   * 캐시 키를 생성합니다.
   */
  private getCacheKey(imageId: string): string {
    return `profile_${imageId}`;
  }

  /**
   * 캐시 항목이 유효한지 확인합니다.
   */
  private isValidCacheEntry(entry: CacheEntry): boolean {
    const now = Date.now();
    return (now - entry.timestamp) < this.CACHE_EXPIRY_TIME;
  }

  private getEntryUrl(entry: CacheEntry): string {
    if (entry.isExternal) return entry.url;
    if (entry.objectUrl) return entry.objectUrl;
    if (entry.blob) {
      const generated = URL.createObjectURL(entry.blob);
      entry.objectUrl = generated;
      return generated;
    }
    return entry.url;
  }

  private releaseEntryResources(entry: CacheEntry): void {
    if (entry.objectUrl) {
      URL.revokeObjectURL(entry.objectUrl);
      entry.objectUrl = undefined;
    }
  }

  /**
   * 기본 이미지 URL을 반환합니다.
   */
  private async getDefaultImageUrl(): Promise<string> {
    // 기본 이미지는 basic-0 사용 (프로필 이미지가 선택되지 않았을 때)
    return await this.getImage('basic-0');
  }

  /**
   * 캐시 통계를 반환합니다.
   */
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }

  /**
   * 모든 캐시를 정리합니다.
   */
  clearCache(): void {
    this.cache.forEach((entry) => {
      this.releaseEntryResources(entry);
    });
    this.cache.clear();
    console.log(`🧹 전체 캐시 정리 완료`);
  }
}

// 싱글톤 인스턴스
export const imageCacheService = new ImageCacheService();

// 프로필 이미지 ID 목록 (basic-1부터 basic-30까지, 총 30개)
export const PROFILE_IMAGE_IDS = Array.from({ length: 30 }, (_, i) => `basic-${i + 1}`);

// 주기적으로 캐시 정리 (10분마다)
setInterval(() => {
  imageCacheService.cleanupExpiredCache();
}, 10 * 60 * 1000);
